import {Inject, Injectable} from '@angular/core';
import {DOCUMENT} from '@angular/common';
import {DefaultUrlSerializer, UrlTree} from '@angular/router';

const TENANT_PREFIX_REGEX = /^\/\d+(?=\/|\?|#|$)/;

@Injectable()
export class TenantUrlSerializer extends DefaultUrlSerializer {
    private readonly tenantPrefix: string | null;

    constructor(@Inject(DOCUMENT) private readonly document: Document) {
        super();
        const pathname = document && document.location ? document.location.pathname : '';
        this.tenantPrefix = this.extractTenantPrefix(pathname);
    }

    parse(url: string): UrlTree {
        const normalized = this.stripTenantPrefix(url || '');
        return super.parse(normalized);
    }

    serialize(tree: UrlTree): string {
        const serialized = super.serialize(tree);
        return this.reapplyTenantPrefix(serialized);
    }

    private stripTenantPrefix(url: string): string {
        if (!url) {
            return url;
        }

        const hasLeadingSlash = url.startsWith('/');
        const workingUrl = hasLeadingSlash ? url : `/${url}`;
        const match = workingUrl.match(TENANT_PREFIX_REGEX);

        if (!match) {
            return url;
        }

        const stripped = workingUrl.substring(match[0].length) || '/';

        if (hasLeadingSlash) {
            return stripped.startsWith('/') ? stripped : `/${stripped}`;
        }

        return stripped.startsWith('/') ? stripped.substring(1) : stripped;
    }

    private reapplyTenantPrefix(url: string): string {
        if (!this.tenantPrefix || !url) {
            return url;
        }

        if (url === '/' || url === this.tenantPrefix) {
            return this.tenantPrefix;
        }

        if (url.startsWith(this.tenantPrefix + '/') || url.startsWith(`${this.tenantPrefix}?`) || url.startsWith(`${this.tenantPrefix}#`)) {
            return url;
        }

        const normalized = url.startsWith('/') ? url.substring(1) : url;
        return `${this.tenantPrefix}/${normalized}`;
    }

    private extractTenantPrefix(pathname: string): string | null {
        if (!pathname) {
            return null;
        }

        const match = pathname.match(TENANT_PREFIX_REGEX);
        return match ? match[0] : null;
    }
}
